import { roleCan } from './access';
import { ROUTES_BATIMENT, ROUTES_MAGASIN } from './modules';
import { C } from '../theme/palette';

/**
 * Entrées du menu « Plus », partagées entre la page Plus (mobile) et la
 * navigation latérale (écran large) : une seule liste, pour que les deux
 * montrent les mêmes portes selon le rôle et l'univers.
 */

/** Modules mis en avant, par univers : [route, icône, libellé, couleur]. */
const MODULES = {
  // Au magasin, « Ajouter un article », « Ouverture compte » et « Remises
  // clients » ont leur porte, comme dans l'ancienne version : le patron les
  // cherchait au menu (« il manque la remise par client, l'ouverture du
  // compte client »).
  magasin: [
    ['stock', '📦', 'Stock & ventes', C.navy],
    ['articles', '➕', 'Ajouter un article', C.teal],
    ['equipe', '👥', 'Équipe & paie', C.amber],
    ['formation', '🎓', 'Formation', C.purple],
    ['clients', '👥', 'Clients & comptes', C.purple],
    ['ouverture', '🆕', 'Ouverture compte', C.green],
    ['remises', '%', 'Remises clients', C.amber],
    ['compta', '📊', 'Comptabilité', C.teal],
    ['reglages', '⚙', 'Réglages', C.grey],
  ],
  chantier: [
    ['chantiers', '🏗', 'Chantiers', C.green],
    ['articles', '➕', 'Ajouter un article', C.purple],
    ['equipe', '👷', 'Équipe & paie', C.amber],
    ['formation', '🎓', 'Formation', C.purple],
    ['compta', '📊', 'Comptabilité', C.teal],
    ['stock', '📦', 'Stock', C.navy],
  ],
};

/**
 * Outils et pages secondaires : [route, icône, libellé], dans l'ordre de la
 * maquette V288. Les Statistiques et les Envois automatiques, propres à la
 * production, se glissent sans déplacer les entrées de la maquette.
 */
const outils = (avecIa) => [
  ['recherche', '🔍', 'Rechercher'],
  ['courrier', '✉️', 'Courrier'],
  ['bonsachat', '🧾', 'Bons d’achat'],
  ['envoyer', '📨', 'Envoyer à l’équipe'],
  ['modeles', '📦', 'Modèles chantier'],
  ['suggestions', '💡', 'Suggestions'],
  ['securite', '🔒', 'Sécurité'],
  ['bonc', '📋', 'Bon de commande'],
  ['notifs', '🔔', 'Notifications'],
  ['envois', '📨', 'E-mail et envois'],
  ['journalco', '🔐', 'Journal des connexions'],
  ['presentation', '🎬', 'Présentation (démo / pub)'],
  ['planning', '📆', 'Planning chantiers'],
  ['clients', '👥', 'Clients'],
  ['commandes', '📦', 'Commandes clients'],
  ['livraisons', '🚚', 'Bons de livraison'],
  ['depenses', '💸', 'Dépenses & frais'],
  ['stats', '📊', 'Statistiques'],
  ['import', '📥', 'Reprise de documents'],
  ['rdv', '📅', 'Rendez-vous'],
  ['decharge', '📜', 'Décharge A4'],
  // Comme la maquette, les Soumissions restent au menu avec ou sans Pack IA :
  // le réglage du corps de métier s'y fait sans lui, et la page explique que
  // la lecture automatique est du pack. Le magasin ne les voit pas : c'est un
  // écran du Bâtiment (`lib/modules.js`).
  ['soumissions', '📄', 'Soumissions'],
  ['gps', '📍', 'GPS équipe'],
  // Le Pack IA se facture en sus : sans lui, l'assistant s'efface. Les
  // calculatrices, elles, ne sont pas de l'IA et restent au menu.
  ['outils', avecIa ? '🤖' : '🧮', avecIa ? 'Outils & IA' : 'Outils'],
  ['archives', '🗄', 'Archives (15 ans)'],
  ['abonnement', '⭐', 'Abonnement'],
  ['partage', '📣', "Partager l'application"],
  ['reglages', '⚙', 'Réglages'],
  ['support', '💬', 'Support & aide'],
  ['aide', '❓', 'Aide & guide'],
];

/**
 * Pages absentes du menu de l'autre univers.
 *
 * <p>D'abord les écrans propres à un module (`lib/modules.js`), que la garde
 * des routes ferme aussi : les modèles de chantier, le planning, le bon de
 * commande d'un chantier n'ont pas d'usage en magasin.
 * S'y ajoutent, côté magasin, deux entrées qui n'y sont pas fermées mais
 * déjà présentes ailleurs : les clients, parmi les modules (« Clients &
 * comptes »), et l'espace employé, ouvert aux seuls employés.
 */
const SEULEMENT_CHANTIER = [...ROUTES_BATIMENT, 'ouvrier', 'clients'];
const SEULEMENT_MAGASIN = ROUTES_MAGASIN;

/** L'espace d'employé du patron rattaché à sa fiche, en tête des outils. */
export const ENTREE_ESPACE_TERRAIN = ['ouvrier', '👷', 'Mon espace terrain'];

/**
 * Les entrées du menu Plus ouvertes au rôle courant.
 *
 * @param {object} options
 * @param {boolean} options.mag vrai dans l'univers Magasin
 * @param {boolean} [options.avecIa] vrai quand le Pack IA est actif
 * @param {boolean} [options.espaceTerrain] vrai quand le compte du bureau est
 *        rattaché à sa propre fiche d'employé : son espace s'ouvre d'ici, dans
 *        les deux univers, puisqu'il y pointe et y livre comme un employé
 * @returns {{modules: Array, outils: Array}} modules et outils, filtrés
 */
export function entreesMenuPlus({ mag, avecIa = true, espaceTerrain = false }) {
  const modules = MODULES[mag ? 'magasin' : 'chantier'].filter(([nav]) => roleCan(nav));
  // Une page déjà parmi les modules ne revient pas dans les outils : au
  // magasin, « Réglages » paraissait deux fois.
  const dejaModule = new Set(modules.map(([nav]) => nav));
  const secondaires = outils(avecIa).filter(
    ([nav]) =>
      roleCan(nav) &&
      !dejaModule.has(nav) &&
      !(mag && SEULEMENT_CHANTIER.includes(nav)) &&
      !(!mag && SEULEMENT_MAGASIN.includes(nav)),
  );
  return {
    modules,
    outils: espaceTerrain ? [ENTREE_ESPACE_TERRAIN, ...secondaires] : secondaires,
  };
}

/**
 * Les outils du menu Plus rangés par thème, pour la page Plus : une liste de
 * trente lignes se lisait mal. L'ordre des groupes est celui-ci ; dans un
 * groupe, l'ordre reste celui des outils. Une entrée sans groupe connu va en
 * « Divers ».
 */
export const GROUPES_PLUS = [
  [
    'Travail',
    [
      'ouvrier',
      'recherche',
      'planning',
      'clients',
      'rdv',
      'modeles',
      'soumissions',
      'bonc',
      'bonsachat',
      'commandes',
      'livraisons',
      'outils',
    ],
  ],
  ['Argent', ['depenses', 'stats', 'archives']],
  ['Documents', ['courrier', 'decharge', 'import']],
  ['Équipe', ['envoyer', 'gps', 'journalco']],
  ['Mon compte', ['reglages', 'abonnement', 'envois', 'notifs', 'securite', 'partage']],
  ['Aide & guide', ['aide', 'support', 'suggestions', 'presentation']],
];

/**
 * @param {Array} outils entrées [route, icône, libellé] déjà filtrées
 * @returns {{titre: string, entrees: Array}[]} les groupes non vides, dans l'ordre
 */
export function outilsEnGroupes(outils) {
  const groupes = GROUPES_PLUS.map(([titre, routes]) => ({
    titre,
    entrees: outils.filter(([nav]) => routes.includes(nav)),
  }));
  const connues = new Set(GROUPES_PLUS.flatMap(([, routes]) => routes));
  const divers = outils.filter(([nav]) => !connues.has(nav));
  return [...groupes, { titre: 'Divers', entrees: divers }].filter((g) => g.entrees.length > 0);
}
